import { joiResolver } from '@hookform/resolvers/joi'
import { Button, Icon, IconButton, Modal, Text } from 'components'

import { useForm } from 'react-hook-form'

import { TextField } from 'uikit'

import { guidelines } from './constants'
import joinClassNames from 'utilities/joinClassNames'
import { useChangePassword } from 'services/auth'

import { parseCookies } from 'nookies'
import { AxiosError } from 'axios'
import { useToast } from 'shared/hooks'

import { schema } from './schemas'

import { Loader } from '../../components'

import { useRef } from 'react'

import styles from './styles.module.scss'

type Credentials = {
  current: string
  confirmation: string
  next: string
}

interface ChangePassword {
  onResetPassword: () => void
}

const ChangePassword = ({ onResetPassword }: ChangePassword) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const {
    register,
    handleSubmit,
    watch,
    setError,
    reset,
    formState: { errors, isValid },
  } = useForm<Credentials>({
    mode: 'onChange',
    resolver: joiResolver(schema),
    defaultValues: {
      current: '',
      next: '',
      confirmation: '',
    },
  })

  const { addToast } = useToast()

  const { next, confirmation } = watch()

  const { mutate, isLoading } = useChangePassword()

  const onSubmit = (data: Credentials) => {
    const { accessToken } = parseCookies()

    mutate(
      {
        accessToken,
        currentPassword: data.current,
        newPassword: data.next,
      },
      {
        onSuccess: async () => {
          reset()
          addToast({ message: 'Senha alterada com sucesso.' })

          closeButtonRef?.current?.click()
        },
        onError: (err: AxiosError) => {
          if (err?.response?.status === 401) {
            setError('current', {
              type: 'manual',
              message: 'Senha incorreta. Verifique e tente novamente.',
            })
          } else {
            addToast({
              message: 'Erro ao alterar senha. Tente novamente.',
              type: 'alert',
            })
          }
        },
      },
    )
  }

  const rules = guidelines.map(({ label, test }) => ({
    label,
    validRule: test(next, confirmation),
  }))

  return (
    <>
      {isLoading && <Loader />}

      <div className={styles.header}>
        <Modal.Title>Alterar senha</Modal.Title>
        <Modal.Close>
          <IconButton ref={closeButtonRef}>
            <Icon name="close-xlg" color="element-default" />
          </IconButton>
        </Modal.Close>
      </div>

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.content}>
          <Text weight="regular">
            Atualize sua senha sempre que quiser para manter sua conta segura.
            Escolha uma combinação forte e fácil de lembrar.
          </Text>

          <TextField
            {...register('current')}
            label="Senha atual"
            type="password"
            message={errors?.current?.message}
          />

          <TextField {...register('next')} label="Nova senha" type="password" />

          <TextField
            {...register('confirmation')}
            label="Confirmação de senha"
            type="password"
          />

          <ul>
            {rules.map(({ label, validRule }, index) => {
              const passOrNotColor = validRule ? styles.success : styles.fail

              return (
                <li
                  key={index}
                  className={joinClassNames(
                    (next || confirmation) && passOrNotColor,
                  )}
                >
                  {label}
                </li>
              )
            })}
          </ul>
        </div>

        <Modal.Footer>
          <Button type="button" appearance="tertiary" onClick={onResetPassword}>
            Esqueceu a senha
          </Button>

          <Button
            width={170}
            type="submit"
            disabled={!isValid || rules.some((rule) => !rule.validRule)}
          >
            Salvar nova senha
          </Button>
        </Modal.Footer>
      </form>
    </>
  )
}

export default ChangePassword
